import { useForm } from 'react-hook-form'
import { Button } from '@cocoonspace/ui/components/button'
import { Form } from '@cocoonspace/ui/components/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { SaveIcon } from 'lucide-react'
import * as z from 'zod'

import { workingHoursSchema } from '../../availabilities/schemas'
import { AvailTab } from './avail_tab'
import { BookingWindowTab } from './booking_window_tab'
import { CalendarSync } from './calendar-sync'
import { GeneralTab } from './general_tab'
import { LocationTab } from './location_tab'
// import { VideoCallTab } from './video-call-tab'

export type BookingWindowSchema = z.infer<typeof bookingWindowSchema>
export type LocationCocoonSchema = z.infer<typeof locationCocoonSchema>
export type LocationSchema = z.infer<typeof locationSchema>
export type EditEventTypeFormData = z.infer<typeof schema>

export const bookingWindowSchema = z.object({
	start: z.string(),
	end: z.string(),
})

export const locationCocoonSchema = z.object({
	type: z.enum(['cocoon']),
	venue_id: z.string().min(1),
	space_ids: z.array(z.string().min(1)).min(1),
})

const locationRemoteSchema = z.object({
	type: z.enum(['remote']),
})

export const locationSchema = z.discriminatedUnion('type', [
	locationCocoonSchema,
	locationRemoteSchema,
])

const schema = z.object({
	title: z.string().min(1),
	description: z.string().min(1),
	duration: z.number(),
	meetingBuffer: z.number().optional(),
	locale: z.literal(['fr', 'en']),
	availabilities: workingHoursSchema,
	bookingWindow: bookingWindowSchema,
	location: locationSchema,
	conflictCalendarRefs: z
		.array(z.object({ id: z.string(), type: z.enum(['google', 'outlook']) }))
		.nullish(),
	// videoCallService: z.enum(['google-meet', 'zoom', 'teams']).nullable(),
	enabled: z.boolean(),
})
// .superRefine((data, ctx) => {
// 	if (data.location.type === 'remote' && !data.videoCallService) {
// 		ctx.addIssue({
// 			code: 'custom',
// 			path: ['videoCallService'],
// 			message: 'Vous devez sélectionner un service de visioconférence',
// 		})
// 	}
// })

export const EventTypeForm = ({
	defaultValues,
	allowPristineSubmit = false,
	isLoading,
	onSubmit,
}: {
	isLoading: boolean
	defaultValues?: Partial<EditEventTypeFormData>
	allowPristineSubmit?: boolean
	onSubmit: (data: EditEventTypeFormData) => Promise<void>
}) => {
	const { t } = useLingui()

	const form = useForm<EditEventTypeFormData>({
		resolver: zodResolver(schema),
		values: {
			title: defaultValues?.title ?? '',
			description: defaultValues?.description ?? '',
			enabled: defaultValues?.enabled ?? true,
			locale: defaultValues?.locale ?? 'fr',
			duration: defaultValues?.duration ?? 30,
			meetingBuffer: defaultValues?.meetingBuffer ?? undefined,
			availabilities: defaultValues?.availabilities ?? {
				mon: [{ startTime: '09:00', endTime: '18:00' }],
				tue: [{ startTime: '09:00', endTime: '18:00' }],
				wed: [{ startTime: '09:00', endTime: '18:00' }],
				thu: [{ startTime: '09:00', endTime: '18:00' }],
				fri: [{ startTime: '09:00', endTime: '18:00' }],
				sat: [],
				sun: [],
			},
			bookingWindow: defaultValues?.bookingWindow ?? {
				start: 'P7D',
				end: 'P2M',
			},
			location: defaultValues?.location as any,
			conflictCalendarRefs: defaultValues?.conflictCalendarRefs ?? [],
			// videoCallService: defaultValues?.videoCallService ?? null,
		},
	})

	const handleSubmit = (data: EditEventTypeFormData) => {
		return onSubmit(data).then(() => {
			form.reset({})
		})
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSubmit)}
				className='flex h-full flex-1 flex-col'
			>
				<fieldset
					disabled={isLoading}
					className='flex h-full flex-1 flex-col divide-y'
				>
					<div className='flex flex-1 flex-col divide-y overflow-y-auto'>
						<GeneralTab />
						<LocationTab />
						<AvailTab />
						<CalendarSync />
						<BookingWindowTab />
						{/* <VideoCallTab /> */}
					</div>

					<div className='relative p-4'>
						{Object.keys(form.formState.errors).length > 0 && (
							<div className='bg-destructive/10 text-destructive mb-2 rounded p-3 py-2 text-xs'>
								{t`Le formulaire est incomplet ou contient des erreurs`}
							</div>
						)}

						<Button
							className='w-full'
							type='submit'
							size='sm'
							disabled={!allowPristineSubmit && !form.formState.isDirty}
						>
							<SaveIcon />
							{t`Enregistrer`}
						</Button>
					</div>
				</fieldset>
			</form>
		</Form>
	)
}
